// src/components/player/PlayerGameLogsTab.js
// PHASE 4: Refactored to use combined game-logs-full endpoint
import React, { useState, useEffect, useMemo } from 'react';
import { playersAPI } from '../../services/apiService';
import { dynastyTheme } from '../../services/colorService';
import { DynastyTable } from '../../services/tableService';
import { createGameLogsColumns } from '../../services/tables/playerColumns';
import { computeTotals } from '../../services/tables/statTotals';
import { getCurrentSeason } from '../../utils/seasonUtils';

// Import all 4 tile components
import Tile1Performance30d from './tiles/Tile1Performance30d';
import Tile1Performance30dPitcher from './tiles/Tile1Performance30dPitcher';
import Tile2ZScoreConventional from './tiles/Tile2ZScoreConventional';
import Tile2ZScoreConventionalPitcher from './tiles/Tile2ZScoreConventionalPitcher';
import Tile3ZScoreSkill from './tiles/Tile3ZScoreSkill';
import Tile3ZScoreSkillPitcher from './tiles/Tile3ZScoreSkillPitcher';
import Tile4TrendVelocity from './tiles/Tile4TrendVelocity';
import Tile4TrendVelocityPitcher from './tiles/Tile4TrendVelocityPitcher';

/**
 * PlayerGameLogsTab - PHASE 4 Refactored
 * 
 * BEFORE: Made 2 API calls
 *   1. game_logs received as prop
 *   2. Separate tile analytics fetch
 * 
 * AFTER: Makes 1 API call
 *   - getPlayerGameLogsFull() returns both game logs + tile analytics (~20KB)
 */
const PlayerGameLogsTab = ({ playerId, leagueId, isPitcher }) => {
  const [loading, setLoading] = useState(true);
  const [gameLogs, setGameLogs] = useState([]);
  const [tileAnalytics, setTileAnalytics] = useState(null);
  // Cluster-D.1: league_scoring bundled into game-logs-full response so the pitcher
  // performance tile can render columns from league_settings.scoring_categories.
  const [leagueScoring, setLeagueScoring] = useState(null);
  const [error, setError] = useState(null);

  // Fetch game logs + tile analytics in single API call
  useEffect(() => {
    const fetchGameLogsAndAnalytics = async () => {
      if (!playerId) {
        console.error('❌ No player ID provided');
        setError('No player ID provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`🔍 Fetching game logs + analytics for player ${playerId}${leagueId ? ` in league ${leagueId}` : ''}`);
        
        // PHASE 4: Single combined API call
        const response = await playersAPI.getPlayerGameLogsFull(
          playerId,
          leagueId,
          null, // season (use current)
          20    // limit
        );
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch game logs data');
        }
        
        console.log('📊 Game Logs Full Response:', {
          game_logs: response.game_logs?.length || 0,
          has_analytics: !!response.tile_analytics,
          is_pitcher: response.is_pitcher
        });
        
        // Extract game logs and tile analytics
        setGameLogs(response.game_logs || []);
        setTileAnalytics(response.tile_analytics || {});
        setLeagueScoring(response.league_scoring || null);
        
        // Debug tile data
        const analytics = response.tile_analytics || {};
        if (analytics.performance_30d) {
          console.log('✅ Tile 1 (Performance 30d):', !!analytics.performance_30d.player);
        }
        if (analytics.z_score_conventional) {
          console.log('✅ Tile 2 (Z-Score Conventional):', !!analytics.z_score_conventional.player);
        }
        if (analytics.z_score_skill) {
          console.log('✅ Tile 3 (Z-Score Skill):', !!analytics.z_score_skill.player);
        }
        if (analytics.trend_velocity) {
          console.log('✅ Tile 4 (Trend Velocity):', !!analytics.trend_velocity.trends);
        }
        
      } catch (error) {
        console.error('❌ Failed to fetch game logs + analytics:', error);
        setError(`Failed to load data: ${error.message || 'Unknown error'}`);
        setGameLogs([]);
        setTileAnalytics(null);
      } finally {
        setLoading(false);
      }
    };

    fetchGameLogsAndAnalytics();
  }, [playerId, leagueId, isPitcher]);

  // Get columns (must be before early returns to satisfy hooks rules)
  const columns = createGameLogsColumns(isPitcher);

  // DD-MODAL P5 fix (2026-07-15): title the table with the season the games are
  // actually FROM (derived from the data), not the global getCurrentSeason().
  // The endpoint defaults to the league's current_season (Bible Rule 6), so a
  // league whose season differs from the global one was mislabeled. Falls back to
  // getCurrentSeason() only when there are no games to read a year from.
  const gameLogsSeason = useMemo(() => {
    const withDate = (gameLogs || []).find((g) => g?.game_date && !Number.isNaN(new Date(g.game_date).getFullYear()));
    return withDate ? new Date(withDate.game_date).getFullYear() : getCurrentSeason();
  }, [gameLogs]);

  // Compute season totals row
  const seasonTotals = useMemo(() => {
    if (!gameLogs || gameLogs.length === 0) return null;

    // Sort by date to find the most recent game (for cumulative season_stats)
    const sorted = [...gameLogs].sort((a, b) => new Date(b.game_date) - new Date(a.game_date));
    const latest = sorted[0];

    if (isPitcher) {
      // DD-STAT-CONSOLIDATION: TOTAL row via the SSoT engine over each game's `pitching`
      // components — sums the full locked set (incl R/HR/L/HLD/BS/GS) AND recomputes
      // ERA/WHIP from the summed ER/IP/H/BB. games_played isn't per-game, so G is
      // supplied separately as the row count.
      const pitchingTotals = computeTotals(gameLogs, {
        statType: 'pitching',
        getComponents: (g) => g.pitching,
      });
      return {
        game_date: 'TOTAL',
        opponent: null,
        mlb_team: null,
        games_total: gameLogs.length, // G column reads this (rows are per-game)
        pitching: pitchingTotals,
      };
    }

    // Hitter totals
    const sum = (key) => gameLogs.reduce((acc, g) => acc + (g?.hitting?.[key] || 0), 0);
    return {
      game_date: 'TOTAL',
      opponent: null,
      mlb_team: null,
      hitting: {
        at_bats: sum('at_bats'),
        runs: sum('runs'),
        hits: sum('hits'),
        doubles: sum('doubles'),
        triples: sum('triples'),
        home_runs: sum('home_runs'),
        rbi: sum('rbi'),
        stolen_bases: sum('stolen_bases'),
        caught_stealing: sum('caught_stealing'),
        walks: sum('walks'),
        strikeouts: sum('strikeouts')
      },
      season_stats: {
        avg: latest?.season_stats?.avg ?? 0,
        obp: latest?.season_stats?.obp ?? 0,
        ops: latest?.season_stats?.ops ?? 0
      }
    };
  }, [gameLogs, isPitcher]);

  // Show error state if fetch failed
  if (error) {
    return (
      <div className={dynastyTheme.components.section}>
        <div className={dynastyTheme.components.card.base}>
          <p className="text-red-500">❌ {error}</p>
        </div>
      </div>
    );
  }

  // Accent border colors for each tile category
  const accentSuccess = dynastyTheme.colors?.success || '#10b981';
  const accentInfo = dynastyTheme.colors?.info || '#3b82f6';
  const accentPrimary = dynastyTheme.colors?.primary || '#eab308';
  const accentError = dynastyTheme.colors?.error || '#ef4444';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', padding: 8, gap: 6 }}>
      {/* 4 Analytics Tiles — FIXED, never scroll */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, flexShrink: 0 }}>
        {/* Tile 1: 30-Day Performance — green accent */}
        <div style={{ borderLeft: `3px solid ${accentSuccess}`, borderRadius: 8, overflow: 'hidden' }}>
          {isPitcher ? (
            <Tile1Performance30dPitcher data={tileAnalytics?.performance_30d} loading={loading} error={error} leagueScoring={leagueScoring} />
          ) : (
            <Tile1Performance30d data={tileAnalytics?.performance_30d} loading={loading} error={error} />
          )}
        </div>

        {/* Tile 2: Z-Score Conventional — blue accent */}
        <div style={{ borderLeft: `3px solid ${accentInfo}`, borderRadius: 8, overflow: 'hidden' }}>
          {isPitcher ? (
            <Tile2ZScoreConventionalPitcher data={tileAnalytics?.z_score_conventional} loading={loading} error={error} />
          ) : (
            <Tile2ZScoreConventional data={tileAnalytics?.z_score_conventional} loading={loading} error={error} />
          )}
        </div>

        {/* Tile 3: Z-Score Skill — yellow accent */}
        <div style={{ borderLeft: `3px solid ${accentPrimary}`, borderRadius: 8, overflow: 'hidden' }}>
          {isPitcher ? (
            <Tile3ZScoreSkillPitcher data={tileAnalytics?.z_score_skill} loading={loading} error={error} />
          ) : (
            <Tile3ZScoreSkill data={tileAnalytics?.z_score_skill} loading={loading} error={error} />
          )}
        </div>

        {/* Tile 4: Trend Velocity — red accent */}
        <div style={{ borderLeft: `3px solid ${accentError}`, borderRadius: 8, overflow: 'hidden' }}>
          {isPitcher ? (
            <Tile4TrendVelocityPitcher data={tileAnalytics?.trend_velocity} loading={loading} error={error} />
          ) : (
            <Tile4TrendVelocity data={tileAnalytics?.trend_velocity} loading={loading} error={error} />
          )}
        </div>
      </div>

      {/* Dynasty Table — fills remaining height, ONLY this scrolls */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <DynastyTable
          title={`${gameLogsSeason} Season (${gameLogs?.length || 0} games)`}
          data={gameLogs || []}
          columns={columns}
          minWidth="500px"
          fillHeight={true}
          enableHorizontalScroll={true}
          enableVerticalScroll={true}
          stickyHeader={true}
          initialSort={{ key: 'game_date', direction: 'asc' }}
          showTotals={!!seasonTotals}
          totalsRow={seasonTotals}
          titlePadding="py-1.5"
        />
      </div>
    </div>
  );
};

export default PlayerGameLogsTab;